import React, { useEffect, useMemo, useState } from "react";

type Status = "Vacant" | "Occupied";
type Room = { id: number; number: string; status: Status; guest?: string };

type Reservation = {
  id: string;
  roomNumber: string;
  guest: string;
  from: string;
  to: string;
  rate?: number; // €/noapte (fallback 100)
};

type Guest = { id: string; name: string; phone?: string };

const LS_ROOMS = "hotelvpf.rooms";
const LS_RES = "hotelvpf.reservations";
const LS_GUESTS = "hotelvpf.guests";

const initialRooms: Room[] = [
  { id: 1, number: "101", status: "Vacant" },
  { id: 2, number: "102", status: "Occupied", guest: "John Smith" },
  { id: 3, number: "103", status: "Vacant" },
];

export default function HotelDashboardEN() {
  // ---------- State + Persistence ----------
  const [rooms, setRooms] = useState<Room[]>(() => {
    const raw = localStorage.getItem(LS_ROOMS);
    return raw ? JSON.parse(raw) : initialRooms;
  });
  const [reservations, setReservations] = useState<Reservation[]>(() => {
    const raw = localStorage.getItem(LS_RES);
    return raw ? JSON.parse(raw) : [];
  });
  const [guests, setGuests] = useState<Guest[]>(() => {
    const raw = localStorage.getItem(LS_GUESTS);
    return raw ? JSON.parse(raw) : [];
  });

  useEffect(
    () => localStorage.setItem(LS_ROOMS, JSON.stringify(rooms)),
    [rooms]
  );
  useEffect(
    () => localStorage.setItem(LS_RES, JSON.stringify(reservations)),
    [reservations]
  );
  useEffect(
    () => localStorage.setItem(LS_GUESTS, JSON.stringify(guests)),
    [guests]
  );

  // ---------- Filters & Search ----------
  const [filterRooms, setFilterRooms] = useState<"All" | Status>("All");
  const [searchRooms, setSearchRooms] = useState("");
  const [searchRes, setSearchRes] = useState("");
  const [searchGuests, setSearchGuests] = useState("");

  const filteredRooms = useMemo(() => {
    return rooms.filter((r) => {
      const okStatus = filterRooms === "All" || r.status === filterRooms;
      const s = searchRooms.toLowerCase();
      const okSearch =
        !s ||
        r.number.toLowerCase().includes(s) ||
        (r.guest ?? "").toLowerCase().includes(s);
      return okStatus && okSearch;
    });
  }, [rooms, filterRooms, searchRooms]);

  const filteredRes = useMemo(() => {
    const s = searchRes.toLowerCase();
    return reservations.filter(
      (r) =>
        !s ||
        r.roomNumber.toLowerCase().includes(s) ||
        r.guest.toLowerCase().includes(s)
    );
  }, [reservations, searchRes]);

  const filteredGuests = useMemo(() => {
    const s = searchGuests.toLowerCase();
    return guests.filter(
      (g) =>
        !s ||
        g.name.toLowerCase().includes(s) ||
        (g.phone ?? "").toLowerCase().includes(s)
    );
  }, [guests, searchGuests]);

  // ---------- Room Actions ----------
  const checkIn = (id: number) => {
    const guest = window.prompt("Guest name for check-in:");
    if (!guest) return;
    setRooms((rs) =>
      rs.map((r) => (r.id === id ? { ...r, status: "Occupied", guest } : r))
    );
  };
  const checkOut = (id: number) => {
    if (!window.confirm("Confirm check-out?")) return;
    setRooms((rs) =>
      rs.map((r) =>
        r.id === id ? { ...r, status: "Vacant", guest: undefined } : r
      )
    );
  };

  // ---------- Reservation Actions ----------
  const [showAdd, setShowAdd] = useState(false);
  const [resRoom, setResRoom] = useState("");
  const [resGuest, setResGuest] = useState("");
  const [resFrom, setResFrom] = useState("");
  const [resTo, setResTo] = useState("");

  const openAdd = () => {
    setResRoom(rooms[0]?.number ?? "");
    setResGuest("");
    setResFrom("");
    setResTo("");
    setShowAdd(true);
  };
  const saveReservation = () => {
    if (!resRoom || !resGuest || !resFrom || !resTo) {
      alert("Please complete all fields.");
      return;
    }
    setReservations((rz) => [
      ...rz,
      {
        id: crypto.randomUUID(),
        roomNumber: resRoom,
        guest: resGuest,
        from: resFrom,
        to: resTo,
      },
    ]);
    setShowAdd(false);
  };
  const deleteReservation = (id: string) => {
    if (!window.confirm("Delete reservation?")) return;
    setReservations((rz) => rz.filter((r) => r.id !== id));
  };

  // ---------- Guest Actions ----------
  const addGuest = () => {
    const name = window.prompt("Guest name:");
    if (!name) return;
    const phone = window.prompt("Phone (optional):") || undefined;
    setGuests((gs) => [...gs, { id: crypto.randomUUID(), name, phone }]);
  };
  const deleteGuest = (id: string) => {
    if (!window.confirm("Delete guest?")) return;
    setGuests((gs) => gs.filter((g) => g.id !== id));
  };

  const roomNumbers = rooms.map((r) => r.number);

  // === KPI calc === (pune exact aici, înainte de return)
  const DEFAULT_RATE = 100; // €/noapte
  const CURRENCY = "€";
  const fmt = (n: number) => CURRENCY + n.toFixed(0);

  const isToday = (from: string, to: string) => {
    const t = new Date().setHours(0, 0, 0, 0);
    const f = new Date(from).setHours(0, 0, 0, 0);
    const td = new Date(to).setHours(0, 0, 0, 0);
    return t >= f && t < td; // [from, to)
  };

  const kpi = useMemo(() => {
    const totalRooms = rooms.length;
    const occupiedRooms = rooms.filter((r) => r.status === "Occupied").length;
    const occRate = totalRooms ? occupiedRooms / totalRooms : 0;

    const todaysRes = reservations.filter((r) => isToday(r.from, r.to));
    const soldToday = todaysRes.length;
    const revenueToday = todaysRes.reduce(
      (s, r) => s + (r.rate ?? DEFAULT_RATE),
      0
    );

    const adr = soldToday ? revenueToday / soldToday : 0;
    const revpar = adr * occRate;

    return { totalRooms, occupiedRooms, occRate, adr, revpar, revenueToday };
  }, [rooms, reservations]);

  const KpiCard = ({
    title,
    value,
    sub,
  }: {
    title: string;
    value: string;
    sub?: string;
  }) => (
    <div className="bg-white rounded shadow p-4">
      <div className="text-sm text-gray-500">{title}</div>
      <div className="text-2xl font-semibold text-gray-800">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="p-6 space-y-12 bg-gray-50 min-h-screen">
      {/* ===== Dashboard ===== */}
      {/* KPI BAR */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          title="Occupancy"
          value={`${(kpi.occRate * 100).toFixed(0)}%`}
          sub={`${kpi.occupiedRooms}/${kpi.totalRooms} rooms`}
        />
        <KpiCard title="ADR" value={fmt(kpi.adr)} sub="Avg Daily Rate" />
        <KpiCard
          title="RevPAR"
          value={fmt(kpi.revpar)}
          sub="Rev/Available Room"
        />
        <KpiCard
          title="Revenue Today"
          value={fmt(kpi.revenueToday)}
          sub="Booked for today"
        />
      </section>
      {/* ---------- Rooms ---------- */}
      <section id="rooms" className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-700 border-b border-gray-200 pb-1">
          Rooms
        </h2>
        <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded shadow">
          <div className="flex items-center gap-2">
            <span className="font-semibold">Filter</span>
            <select
              className="border rounded px-3 py-1 min-w-[100px]"
              value={filterRooms}
              onChange={(e) => setFilterRooms(e.target.value as any)}
            >
              <option value="All">All</option>
              <option value="Vacant">Vacant</option>
              <option value="Occupied">Occupied</option>
            </select>
          </div>
          <input
            placeholder="Search by room or guest"
            className="border rounded px-3 py-1 flex-1 min-w-[200px]"
            value={searchRooms}
            onChange={(e) => setSearchRooms(e.target.value)}
          />
        </div>

        <table className="w-full border bg-white rounded shadow">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-4 py-2">Room</th>
              <th className="border px-4 py-2">Status</th>
              <th className="border px-4 py-2">Guest</th>
              <th className="border px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRooms.map((r) => (
              <tr key={r.id}>
                <td className="border px-4 py-2">{r.number}</td>
                <td className="border px-4 py-2">{r.status}</td>
                <td className="border px-4 py-2">{r.guest ?? ""}</td>
                <td className="border px-4 py-2 space-x-2">
                  <button
                    className={`px-3 py-1 rounded text-white ${
                      r.status === "Vacant"
                        ? "bg-blue-500 hover:bg-blue-600"
                        : "bg-blue-300 cursor-not-allowed"
                    }`}
                    disabled={r.status !== "Vacant"}
                    onClick={() => r.status === "Vacant" && checkIn(r.id)}
                  >
                    Check-in
                  </button>
                  <button
                    className={`px-3 py-1 rounded text-white ${
                      r.status === "Occupied"
                        ? "bg-red-500 hover:bg-red-600"
                        : "bg-red-300 cursor-not-allowed"
                    }`}
                    disabled={r.status !== "Occupied"}
                    onClick={() => r.status === "Occupied" && checkOut(r.id)}
                  >
                    Check-out
                  </button>
                </td>
              </tr>
            ))}
            {filteredRooms.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-4">
                  No rooms found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      {/* ---------- Reservations ---------- */}
      <section id="reservations" className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-700 border-b border-gray-200 pb-1">
          Reservations
        </h2>
        <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded shadow">
          <input
            placeholder="Search by room or guest"
            className="border rounded px-3 py-1 flex-1 min-w-[200px]"
            value={searchRes}
            onChange={(e) => setSearchRes(e.target.value)}
          />
          <button
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-1 rounded"
            onClick={openAdd}
          >
            Add Reservation
          </button>
        </div>

        {showAdd && (
          <div className="bg-yellow-50 border rounded p-4 space-y-3 shadow">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="block text-sm font-semibold mb-1">Room</label>
                <select
                  className="border rounded px-2 py-1 w-full"
                  value={resRoom}
                  onChange={(e) => setResRoom(e.target.value)}
                >
                  {roomNumbers.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">
                  Guest
                </label>
                <input
                  className="border rounded px-2 py-1 w-full"
                  value={resGuest}
                  onChange={(e) => setResGuest(e.target.value)}
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">
                  Arrival
                </label>
                <input
                  type="date"
                  className="border rounded px-2 py-1 w-full"
                  value={resFrom}
                  onChange={(e) => setResFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">
                  Departure
                </label>
                <input
                  type="date"
                  className="border rounded px-2 py-1 w-full"
                  value={resTo}
                  onChange={(e) => setResTo(e.target.value)}
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded"
                  onClick={saveReservation}
                >
                  Save
                </button>
                <button
                  className="border px-3 py-1 rounded"
                  onClick={() => setShowAdd(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <table className="w-full border bg-white rounded shadow">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-4 py-2">Room</th>
              <th className="border px-4 py-2">Guest</th>
              <th className="border px-4 py-2">Arrival</th>
              <th className="border px-4 py-2">Departure</th>
              <th className="border px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRes.map((r) => (
              <tr key={r.id}>
                <td className="border px-4 py-2">{r.roomNumber}</td>
                <td className="border px-4 py-2">{r.guest}</td>
                <td className="border px-4 py-2">{r.from}</td>
                <td className="border px-4 py-2">{r.to}</td>
                <td className="border px-4 py-2">
                  <button
                    className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded"
                    onClick={() => deleteReservation(r.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {filteredRes.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  No reservations found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      {/* ---------- Guests ---------- */}
      <section id="guests" className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-700 border-b border-gray-200 pb-1">
          Guests
        </h2>
        <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded shadow">
          <input
            placeholder="Search name or phone"
            className="border rounded px-3 py-1 flex-1 min-w-[200px]"
            value={searchGuests}
            onChange={(e) => setSearchGuests(e.target.value)}
          />
          <button
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-1 rounded"
            onClick={addGuest}
          >
            Add Guest
          </button>
        </div>

        <table className="w-full border bg-white rounded shadow">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-4 py-2">Name</th>
              <th className="border px-4 py-2">Phone</th>
              <th className="border px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredGuests.map((g) => (
              <tr key={g.id}>
                <td className="border px-4 py-2">{g.name}</td>
                <td className="border px-4 py-2">{g.phone ?? ""}</td>
                <td className="border px-4 py-2">
                  <button
                    className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded"
                    onClick={() => deleteGuest(g.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {filteredGuests.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center py-4">
                  No guests found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
